import { useState, useCallback, useMemo, useEffect } from "react";
import styles from "./HolidayTypesManager.module.css";
import AddEditHolidayTypeModal from "../../../components/GeoData/HolidayTypes/AddEditHolidayTypeModal";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import { useToast } from "../../../context/ToastContext";
import {
  useGetHolidayTypes,
  useCreateHolidayType,
  useUpdateHolidayType,
  useHardDeleteHolidayType,
} from "../../../api/geo/useHolidayTypes";

const getDisplayValue = (row, field, pendingChanges) =>
  pendingChanges[row.id]?.[field] ?? row[field];

const HolidayTypesManager = () => {
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");

  const [showAddEdit, setShowAddEdit] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [pendingChanges, setPendingChanges] = useState({});
  const [toggleSaveLoading, setToggleSaveLoading] = useState(false);

  const { data, count, pagination, loading, error, refetch } = useGetHolidayTypes({
    page,
    limit,
    category: categoryFilter || undefined,
    active: activeFilter !== "" ? activeFilter : undefined,
    languageCode: "ENG",
  });

  const rows = Array.isArray(data) ? data : [];

  const { execute: createType, loading: createLoading } = useCreateHolidayType();
  const { execute: updateType, loading: updateLoading } = useUpdateHolidayType();
  const { execute: deleteType, loading: deleteLoading } = useHardDeleteHolidayType();

  const saveLoading = createLoading || updateLoading;
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  const filteredRows = rows.filter((r) => {
    const matchSearch = !search || r.name?.toLowerCase().includes(search.toLowerCase());
    return matchSearch;
  });

  const applyToggle = useCallback((row, field) => {
    setPendingChanges((prev) => {
      const current = prev[row.id] ?? {};
      const serverVal = row[field];
      const pendingVal = current[field];
      const nextVal = pendingVal !== undefined ? !pendingVal : !serverVal;
      return {
        ...prev,
        [row.id]: { ...current, [field]: nextVal },
      };
    });
  }, []);

  // Auto-search: debounce the input so the list filters as you type (no button).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
      setPendingChanges({});
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleAdd = () => {
    setMode("add");
    setSelectedRow(null);
    setShowAddEdit(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowAddEdit(true);
  };

  const handleDeleteClick = (row) => setRowToDelete(row);

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteType(rowToDelete.id);
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[rowToDelete.id];
        return next;
      });
      refetch();
      setRowToDelete(null);
      showToast("Holiday/theme type deleted successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete type", "error");
    }
  };

  const handleSaveToggles = async () => {
    const ids = Object.keys(pendingChanges);
    if (ids.length === 0) return;
    setToggleSaveLoading(true);
    try {
      for (const id of ids) {
        const patch = pendingChanges[id];
        const row = rows.find((r) => String(r.id) === String(id));
        const payload = {
          active: patch.active ?? row?.active,
        };
        await updateType(id, payload);
      }
      setPendingChanges({});
      refetch();
      showToast("Changes saved successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save changes", "error");
    } finally {
      setToggleSaveLoading(false);
    }
  };

  const handleSave = async (payload, editingId = null) => {
    try {
      // Modal passes editingId: only update when editing an existing row
      if (editingId != null) {
        await updateType(editingId, payload);
        showToast("Holiday/theme type updated successfully", "success");
      } else {
        await createType(payload);
        showToast("Holiday/theme type created successfully", "success");
      }
      setShowAddEdit(false);
      refetch();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save holiday/theme type", "error");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Holiday & Theme Types</div>
        <button className={styles.add} onClick={handleAdd}>
          + Add Type
        </button>
      </div>

      <div className={styles.tagLine}>
        <span className={styles.tagIcon} aria-hidden>ℹ</span>
        <span>Holiday and theme types categorize offers (e.g. Ski, Beach, Christmas). Used for filtering and marketing.</span>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <SelectBox
          placeholder="Category: All"
          value={categoryFilter}
          options={[
            { label: "All", value: "" },
            { label: "Holiday", value: "Holiday" },
            { label: "Theme", value: "Theme" },
          ]}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
        <SelectBox
          placeholder="Status: All"
          value={activeFilter}
          options={[
            { label: "All", value: "" },
            { label: "Active", value: "true" },
            { label: "Inactive", value: "false" },
          ]}
          onChange={(e) => {
            setActiveFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading holiday/theme types…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {filteredRows.length} of {pagination?.total ?? count ?? 0} types
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Icon</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={styles.noResults}>
                      No holiday/theme types found.
                    </td>
                  </tr>
                ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name ?? "—"}</td>
                    <td className={styles.badge}>{row.category ?? "—"}</td>
                    <td className={styles.iconCell}>
                      <span className={styles.iconEmoji}>{row.icon || "—"}</span>
                    </td>
                    <td>
                      <ToggleSwitch
                        checked={!!getDisplayValue(row, "active", pendingChanges)}
                        onChange={() => applyToggle(row, "active")}
                      />
                    </td>
                    <td className={styles.rowActions}>
                      <button onClick={() => handleEdit(row)}>Edit</button>
                      <button onClick={() => handleDeleteClick(row)}>Delete</button>
                    </td>
                  </tr>
                )))}
              </tbody>
              </table>
            </div>

            <div className={styles.footerBar}>
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Page {pagination?.page ?? page} of {pagination?.totalPages || 1} ({pagination?.total ?? count ?? 0} total)
                </span>
                {(pagination?.totalPages ?? 1) > 1 && (
                  <div className={styles.pageNumbers}>
                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
                      ) : (
                        <button
                          key={p}
                          className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                          onClick={() => {
                            setPage(p);
                            setPendingChanges({});
                          }}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.saveChangesBtn}
                onClick={handleSaveToggles}
                disabled={!hasPendingChanges || toggleSaveLoading || saveLoading}
              >
                {toggleSaveLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <AddEditHolidayTypeModal
        key={selectedRow?.id ?? "new"}
        open={showAddEdit}
        onClose={() => setShowAddEdit(false)}
        mode={mode}
        initialData={selectedRow}
        onSave={handleSave}
        saveLoading={saveLoading}
      />

      <DeleteConfirmModal
        open={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        title="Delete holiday/theme type"
        itemName={rowToDelete?.name}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default HolidayTypesManager;
